import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useCurrency } from '../../hooks/useCurrency';
import { ExpenseBreakdownData } from '../../hooks/useChartsData';

interface ExpensesPieChartProps {
  data: ExpenseBreakdownData[];
  formatAmount?: (amount: number) => string;
}

export function ExpensesPieChart({ data, formatAmount }: ExpensesPieChartProps) {
  const { currentCurrency } = useCurrency();
  
  const defaultFormatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-CD', { 
      style: 'currency', 
      currency: currentCurrency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const amountFormatter = formatAmount || defaultFormatAmount;
  
  // Custom label function pour afficher les pourcentages
  const renderCustomLabel = ({ 
    cx, 
    cy, 
    midAngle, 
    innerRadius, 
    outerRadius, 
    percent 
  }: any) => {
    if (percent < 0.05) return null; // Ne pas afficher les labels pour les très petites portions
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="11"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Légende personnalisée responsive
  const renderCustomLegend = (props: any) => {
    const { payload } = props;
    
    return (
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 px-2 pt-4 max-w-full overflow-hidden">
        {payload.map((entry: any, index: number) => (
          <div 
            key={`legend-${index}`}
            className="flex items-center text-xs whitespace-nowrap"
          >
            <span 
              className="w-2.5 h-2.5 rounded-full mr-1.5 flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 dark:text-gray-300 truncate max-w-[100px] sm:max-w-[120px]">
              {entry.value}
            </span>
            <span className="text-gray-500 dark:text-gray-400 ml-1">
              ({entry.payload?.percentage?.toFixed(0)}%)
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={350}>
      <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
        <Pie
          data={data}
          cx="50%"
          cy="45%"
          labelLine={false}
          label={renderCustomLabel}
          outerRadius={90}
          innerRadius={35}
          fill="#8884d8"
          paddingAngle={2}
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={entry.color}
              stroke={entry.color}
              strokeWidth={1}
            />
          ))}
        </Pie>
        
        <Tooltip 
          formatter={(value: number, name: string) => [
            amountFormatter(value),
            name
          ]}
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '12px',
            color: 'hsl(var(--foreground))',
            fontSize: '13px'
          }}
        />
        
        <Legend 
          content={renderCustomLegend}
          verticalAlign="bottom"
        />
      </PieChart>
    </ResponsiveContainer>
  );
}